/**
 * A scenario — "what would happen if" — as a DIFF over the read set.
 *
 * The whole module already had the hard half of this: the engine holds no stored
 * tier per account and recomputes everything from live spend on every call, so a
 * changed input re-renders a complete, honest answer rather than an estimate.
 * What was missing was the ability to change any input other than the tier list.
 *
 * A scenario is therefore a small overlay, not a second code path:
 *
 *   tiers      — reshape the bands            (the tier editor)
 *   balanceBy  — count or booking volume
 *   slots      — MOVE A CSM between tiers     (reassign their seat)
 *   pins       — MOVE ONE CUSTOMER to someone (a pin, not yet written)
 *
 * Every field is optional and absent means "use what is stored". That matters
 * more than it looks: an empty scenario must compute BYTE-IDENTICALLY to the
 * saved model, or the page would quietly disagree with itself the moment someone
 * opened it.
 *
 * NOTHING HERE WRITES. A scenario lives in browser state and dies on reload,
 * which is the property that lets a manager try six ideas without leaving six
 * half-considered versions at the head of an immutable audit trail. Turning one
 * into reality is an explicit, separate action.
 */
import type { PinDto, SlotDto, StoredTierDto } from '../api';

/**
 * A person who does not exist, for "what if we hired someone into Medium".
 *
 * The point of the whole feature: a manager can see which tier a new hire would
 * relieve most, before writing a requisition. The engine needs no special case
 * for it — a hire is a roster entry plus a seat, and both are things the model
 * already takes as overrides.
 *
 * `id` IS the engine key. It is prefixed (`HYPOTHETICAL_PREFIX`) so that a
 * synthetic person can never be mistaken for a real one anywhere downstream: the
 * roster's real keys are external ids, emails and display names, none of which
 * can collide with this. Everything that writes reads the STORED slots rather
 * than the scenario's, so a hire cannot reach the database — and `isHypothetical`
 * exists so that any future save path can assert it rather than hope.
 */
export interface HypotheticalCsm {
  id: string;
  displayName: string;
  /** The tier they would be hired into. Exactly one — a new hire staffs one seat. */
  tierKey: string;
}

/** Marks a CSM key as invented. Never written anywhere. */
export const HYPOTHETICAL_PREFIX = 'hypothetical:';

export function isHypothetical(csmKey: string): boolean {
  return csmKey.startsWith(HYPOTHETICAL_PREFIX);
}

export interface Scenario {
  tiers?: StoredTierDto[];
  balanceBy?: 'count' | 'spend';
  slots?: SlotDto[];
  pins?: PinDto[];
  /** People who do not exist yet. See HypotheticalCsm. */
  hires?: HypotheticalCsm[];
  /**
   * People leaving — `staffing_csm` ROW ids, the same key slots and pins use.
   *
   * The mirror of `hires`, and the question that actually gets asked: somebody
   * gives notice and the team needs to see, today, what their book does to
   * everyone else. Removing them on the Team tab is a WRITE and answers a
   * different question, so it cannot be used to ask this one.
   *
   * A departure REMOVES their seats rather than vacating them, so the tier
   * genuinely shrinks and their book redistributes across whoever is left. That
   * is the shape of the question — the work has to go somewhere. Modelling a
   * backfill is the same departure plus a `hires` entry, which reads as what it
   * is: two decisions, not one.
   */
  departures?: string[];
}

export const EMPTY_SCENARIO: Scenario = {};

/** Is anything actually overridden? Drives the "previewing" banner and Reset. */
export function isScenarioActive(scenario: Scenario): boolean {
  return (
    scenario.tiers !== undefined ||
    scenario.balanceBy !== undefined ||
    scenario.slots !== undefined ||
    scenario.pins !== undefined ||
    (scenario.hires !== undefined && scenario.hires.length > 0) ||
    (scenario.departures !== undefined && scenario.departures.length > 0)
  );
}

/**
 * Add a hypothetical hire to a tier.
 *
 * Numbered from the EXISTING list rather than from a counter, so withdrawing
 * "New hire 2" and adding another gives you a "New hire 2" again instead of a
 * "New hire 3" beside a "New hire 1" — a gap in the numbering reads as somebody
 * having lost a row.
 */
export function proposeHire(
  hires: readonly HypotheticalCsm[],
  tierKey: string,
  displayName?: string,
): HypotheticalCsm[] {
  const used = new Set(hires.map((h) => h.id));
  let n = 1;
  while (used.has(`${HYPOTHETICAL_PREFIX}${n}`)) n += 1;
  return [
    ...hires,
    {
      id: `${HYPOTHETICAL_PREFIX}${n}`,
      displayName: displayName?.trim() || `New hire ${n}`,
      tierKey,
    },
  ];
}

/** Take a hypothetical hire back out. */
export function withdrawHire(
  hires: readonly HypotheticalCsm[],
  id: string,
): HypotheticalCsm[] {
  return hires.filter((h) => h.id !== id);
}

/** Move a hypothetical hire to a different tier — the question is WHERE, after all. */
export function moveHire(
  hires: readonly HypotheticalCsm[],
  id: string,
  tierKey: string,
): HypotheticalCsm[] {
  return hires.map((h) => (h.id === id ? { ...h, tierKey } : h));
}

/**
 * Give a REAL person a seat in a tier they hold none in.
 *
 * The sibling of `moveCsmToTier`, which needs a tier to move FROM and so cannot
 * express "this person staffs nothing today; what if they staffed Medium". That
 * is an ordinary question about a manager, a new starter, or anyone whose seats
 * were just cleared.
 *
 * At the END of the destination, for the same reason every other seat write in
 * this module is: position is load-bearing, and inserting at the front would
 * silently redistribute the existing team's books as a side effect.
 */
export function placeCsmInTier(
  slots: readonly SlotDto[],
  csm: { id: string; displayName: string },
  tierKey: string,
): SlotDto[] {
  if (slots.some((s) => s.csmId === csm.id && s.tierKey === tierKey))
    return [...slots];
  return [
    ...slots,
    {
      tierKey,
      position: slots.filter((s) => s.tierKey === tierKey).length,
      csmId: csm.id,
      csmDisplayName: csm.displayName,
      csmActive: true,
      vacancy: false,
    },
  ];
}

/**
 * Move one CSM's seats from one tier to another.
 *
 * Seats, plural and deliberately: a CSM can hold more than one seat in a tier
 * (two Small seats is how the reference model expressed "spends most of their
 * time here"), and moving the person means moving all of them. Moving one of
 * three would be a different, stranger question that nobody has asked for.
 *
 * The moved seats take positions at the END of the destination tier, because
 * position is load-bearing — the engine breaks capacity ties by FIRST slot, so
 * inserting someone at the front would silently reorder the existing team's
 * distribution as a side effect of a question about one person.
 */
export function moveCsmToTier(
  slots: readonly SlotDto[],
  csmId: string,
  fromTierKey: string,
  toTierKey: string,
): SlotDto[] {
  if (fromTierKey === toTierKey) return [...slots];

  const moving = slots.filter(
    (s) => s.csmId === csmId && s.tierKey === fromTierKey,
  );
  if (moving.length === 0) return [...slots];

  const kept = slots.filter(
    (s) => !(s.csmId === csmId && s.tierKey === fromTierKey),
  );
  // Renumber the source tier so it has no gap where they were. A gap is not an
  // error to the engine, but it makes "position 0, 2, 3" read as a lost seat.
  const renumbered = renumber(kept, fromTierKey);
  const destinationSize = renumbered.filter(
    (s) => s.tierKey === toTierKey,
  ).length;

  return [
    ...renumbered,
    ...moving.map((s, i) => ({
      ...s,
      tierKey: toTierKey,
      position: destinationSize + i,
    })),
  ];
}

/** Close the gaps in one tier's positions, preserving order. */
function renumber(slots: readonly SlotDto[], tierKey: string): SlotDto[] {
  let next = 0;
  return slots
    .slice()
    .sort(
      (a, b) => a.tierKey.localeCompare(b.tierKey) || a.position - b.position,
    )
    .map((s) =>
      s.tierKey === tierKey ? { ...s, position: next++ } : { ...s },
    );
}

/**
 * Propose moving one customer to one CSM — a pin that has not been written.
 *
 * Shaped as a real `PinDto` rather than a lighter "proposed move" type, so the
 * engine sees exactly what it would see after the write. A preview built from a
 * different shape than the thing it previews is a preview of something else.
 *
 * `createdAt` is 'now' only in the sense that it must sort ABOVE the stored
 * pins, so the proposed move leads the list it lands in — the same rule the real
 * pins follow. It is never sent anywhere.
 */
export function proposeMove(
  pins: readonly PinDto[],
  move: {
    customerId: string;
    csmId: string;
    csmDisplayName: string;
    at: string;
  },
): PinDto[] {
  const existing = pins.find((p) => p.customerId === move.customerId);
  const others = pins.filter((p) => p.customerId !== move.customerId);
  return [
    ...others,
    {
      customerId: move.customerId,
      csmId: move.csmId,
      csmDisplayName: move.csmDisplayName,
      csmActive: true,
      // A hand-picked TIER survives a change of owner. The two halves of a pin
      // answer different questions, and "who holds this" is not an opinion about
      // "which band it is served at" — dropping it here would quietly demote a
      // strategic account to its spend band as a side effect of reassigning it.
      tierKey: existing?.tierKey ?? null,
      reason: 'scenario — not saved',
      createdBy: 'scenario',
      createdAt: move.at,
    },
  ];
}

/**
 * Propose PLACING one customer in a tier — the other half of a pin, previewed.
 *
 * Mirrors `proposeMove` exactly, including preserving the half it is not about:
 * placing an account in the strategic tier says nothing about who holds it.
 * `tierKey: null` withdraws the placement and lets the spend band decide again.
 */
export function proposePlacement(
  pins: readonly PinDto[],
  place: { customerId: string; tierKey: string | null; at: string },
): PinDto[] {
  const existing = pins.find((p) => p.customerId === place.customerId);
  const others = pins.filter((p) => p.customerId !== place.customerId);
  // A row overriding nothing is not a weaker pin, it is a row that reads as a
  // decision and changes nothing — cm-api refuses to store one, so a scenario
  // must not compute one either.
  if (place.tierKey === null && (existing?.csmId ?? null) === null) {
    return others;
  }
  return [
    ...others,
    {
      customerId: place.customerId,
      csmId: existing?.csmId ?? null,
      csmDisplayName: existing?.csmDisplayName ?? null,
      csmActive: existing?.csmActive ?? null,
      tierKey: place.tierKey,
      reason: 'scenario — not saved',
      createdBy: 'scenario',
      createdAt: place.at,
    },
  ];
}

/** Undo a proposed move, leaving any STORED pin for that customer alone. */
export function withdrawMove(
  storedPins: readonly PinDto[],
  scenarioPins: readonly PinDto[],
  customerId: string,
): PinDto[] {
  const stored = storedPins.find((p) => p.customerId === customerId);
  const without = scenarioPins.filter((p) => p.customerId !== customerId);
  return stored ? [...without, stored] : without;
}

/**
 * How many things this scenario changes, for a banner that has to be honest.
 *
 * Counted against the STORED state rather than tracked as a running tally: a
 * move and its undo must net to zero, or the banner accumulates a number that
 * only ever grows and stops meaning anything.
 */
/**
 * Model somebody leaving.
 *
 * Idempotent, because the control that calls this is a toggle and double-firing
 * it must not produce two departures for one person — `scenarioChangeCount`
 * counts entries, so that would read as two people leaving.
 */
export function departCsm(
  departures: readonly string[],
  csmId: string,
): string[] {
  return departures.includes(csmId) ? [...departures] : [...departures, csmId];
}

/** Put them back — the notice was withdrawn, or the question is answered. */
export function reinstateCsm(
  departures: readonly string[],
  csmId: string,
): string[] {
  return departures.filter((id) => id !== csmId);
}

export function isDeparted(scenario: Scenario, csmId: string): boolean {
  return scenario.departures?.includes(csmId) ?? false;
}

export function scenarioChangeCount(
  scenario: Scenario,
  stored: {
    tiers: readonly StoredTierDto[];
    slots: readonly SlotDto[];
    pins: readonly PinDto[];
    balanceBy: string;
  },
): number {
  let n = 0;
  if (
    scenario.tiers !== undefined &&
    JSON.stringify(scenario.tiers) !== JSON.stringify(stored.tiers)
  ) {
    n += 1;
  }
  if (
    scenario.balanceBy !== undefined &&
    scenario.balanceBy !== stored.balanceBy
  ) {
    n += 1;
  }
  if (scenario.slots !== undefined) {
    n += countMovedCsms(stored.slots, scenario.slots);
  }
  if (scenario.pins !== undefined) {
    n += countChangedPins(stored.pins, scenario.pins);
  }
  // One per invented person. They have no stored counterpart to diff against —
  // their existence IS the change.
  n += scenario.hires?.length ?? 0;
  // And one per person leaving, for the same reason in reverse. Counted here
  // rather than as the seats they vacate: "Dana leaves" is one decision whether
  // she staffed one tier or three, and counting seats would make a multi-tier
  // CSM's departure read as a bigger change than a single-tier one.
  n += scenario.departures?.length ?? 0;
  return n;
}

/** One per CSM whose set of (tier, count) seats differs from the stored one. */
function countMovedCsms(
  stored: readonly SlotDto[],
  next: readonly SlotDto[],
): number {
  const shape = (slots: readonly SlotDto[]) => {
    const out = new Map<string, string>();
    const byCsm = new Map<string, string[]>();
    for (const s of slots) {
      if (s.csmId === null) continue;
      byCsm.set(s.csmId, [...(byCsm.get(s.csmId) ?? []), s.tierKey]);
    }
    for (const [csmId, tiers] of byCsm) out.set(csmId, tiers.sort().join(','));
    return out;
  };
  const a = shape(stored);
  const b = shape(next);
  let n = 0;
  for (const csmId of new Set([...a.keys(), ...b.keys()])) {
    if (a.get(csmId) !== b.get(csmId)) n += 1;
  }
  return n;
}

/**
 * One per customer whose DECISION differs from the stored one.
 *
 * Both halves, compared as a pair: moving a customer to another CSM and placing
 * it in another tier are two changes to the same customer, and counting the row
 * once would under-report a scenario that did both.
 */
function countChangedPins(
  stored: readonly PinDto[],
  next: readonly PinDto[],
): number {
  const shape = (pins: readonly PinDto[]) =>
    new Map(pins.map((p) => [p.customerId, [p.csmId, p.tierKey] as const]));
  const a = shape(stored);
  const b = shape(next);
  let n = 0;
  for (const customerId of new Set([...a.keys(), ...b.keys()])) {
    const before = a.get(customerId) ?? [null, null];
    const after = b.get(customerId) ?? [null, null];
    if (before[0] !== after[0]) n += 1;
    if (before[1] !== after[1]) n += 1;
  }
  return n;
}
